/**
 * table-filter — lógica pura do filtro de texto da SortableTable (#6).
 *
 * Separado de sortable-table.tsx para ser testável sem mocks de
 * next/navigation (convenção do repo: pure-logic vitest, sem
 * @testing-library/react).
 *
 * Comportamento:
 *   - Case-insensitive E accent-insensitive (NFD + strip combining marks).
 *   - Query quebrada em tokens por whitespace; linha passa se TODO token
 *     aparece em ALGUMA coluna (AND entre tokens, OR entre colunas).
 *   - Valores pesquisáveis por coluna: valor cru do accessor (string/number)
 *     E saída do `format` quando esta é string/number — o usuário encontra
 *     tanto "1234.5" (cru) quanto "1.234,5" (renderizado).
 *   - Colunas `hidden` (accessor-only para sort) ficam fora da busca.
 */

/** Subconjunto estrutural de SortableColumn<T> usado pelo filtro. */
export interface FilterableColumn<T> {
  key: string;
  accessor?: (row: T) => unknown;
  format?: (value: unknown, row: T) => unknown;
  hidden?: boolean;
}

/** Lowercase + remove diacríticos (ç→c, ã→a, é→e...). */
export function normalizeText(value: string): string {
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
}

function searchableValues<T>(row: T, columns: FilterableColumn<T>[]): string[] {
  const out: string[] = [];
  for (const col of columns) {
    if (col.hidden) continue;
    const raw = col.accessor
      ? col.accessor(row)
      : (row as Record<string, unknown>)[col.key];
    if (typeof raw === "string" || typeof raw === "number") {
      out.push(normalizeText(String(raw)));
    }
    if (col.format) {
      const formatted = col.format(raw, row);
      if (typeof formatted === "string" || typeof formatted === "number") {
        const norm = normalizeText(String(formatted));
        if (norm !== out[out.length - 1]) out.push(norm);
      }
    }
  }
  return out;
}

function queryTokens(query: string): string[] {
  return normalizeText(query).split(/\s+/).filter(Boolean);
}

/** True se a linha satisfaz a query (todo token presente em alguma coluna). */
export function rowMatchesFilter<T>(
  row: T,
  columns: FilterableColumn<T>[],
  query: string,
): boolean {
  const tokens = queryTokens(query);
  if (tokens.length === 0) return true;
  const values = searchableValues(row, columns);
  return tokens.every((token) => values.some((v) => v.includes(token)));
}

/**
 * Filtra as linhas pela query. Query vazia/whitespace devolve o MESMO array
 * (identidade preservada — não invalida memos downstream).
 */
export function filterRows<T>(
  rows: T[],
  columns: FilterableColumn<T>[],
  query: string,
): T[] {
  const tokens = queryTokens(query);
  if (tokens.length === 0) return rows;
  return rows.filter((row) => {
    const values = searchableValues(row, columns);
    return tokens.every((token) => values.some((v) => v.includes(token)));
  });
}
